/*
 * Sticky mobile checkout bar - cart page
 * --------------------------------------
 * Pins a bar to the bottom of the viewport on the cart page (mobile only)
 * showing the CART SUBTOTAL and a Checkout button that mirrors the real one.
 *
 * The bar does NOT move the real checkout button (that would detach it from
 * #CartPage-Form and break submission). It renders a proxy button that clicks
 * the real one, so terms-checkbox validation and GoKwik still fire normally.
 *
 * 2026-08 fixes
 *   1. Subtotal reads '.hdt-totals__total-value' (cart.total_price), not
 *      '.hdt-page_cart__total-price' which is the FIRST LINE ITEM's row total.
 *   2. Screen-reader labels are stripped before the money value is read.
 *   3. The proxy resolves whichever checkout control is actually visible -
 *      GoKwik's button first, the native Shopify button as fallback.
 *   4. schedule() pairs rAF with a setTimeout fallback, since rAF is frozen in
 *      background tabs and used to wedge the pending flag permanently.
 *   5. FREEZE FIX: the observer no longer watches attributes. GoKwik runs its
 *      own MutationObserver on document.body which repeatedly re-enables
 *      '.gokwik-checkout button'. Watching class/disabled meant every one of
 *      those flips woke this bar, which wrote into <body>, which woke GoKwik's
 *      observer again - a feedback loop between the two that froze the page.
 *      The disabled -> enabled change is now picked up by the 'gokwikLoaded'
 *      event plus the bounded settle interval instead.
 *
 * LOOP SAFETY:
 *   The bar is appended to <body>, but the MutationObserver watches only
 *   #hdt-page-cart - so the bar's own DOM writes are outside the watched
 *   subtree and cannot retrigger it. Three further guards:
 *     1. observer.disconnect() around every sync, reconnect after
 *     2. markup is only written when the value actually differs
 *     3. updates coalesced through requestAnimationFrame
 *
 * TO REVERT: delete the <script> tag for this file from layout/theme.liquid.
 */
(function () {
  var BAR_ID = 'tu-cart-sticky-bar';
  var STYLE_ID = 'tu-cart-sticky-style';

  var SEL_TOTAL = '.hdt-totals__total-value';
  var SEL_TOTAL_FALLBACK = '[data-cart-subtotal]';

  var CHECKOUT_SELECTORS = ['.gokwik-checkout button', '.hdt-main-cart__button-checkout'];
  var SEL_CHECKOUT_FALLBACK = '.hdt-main-cart__button-checkout';

  var SEL_TOOLBAR = '.hdt-toolbar-mobile, [class*="toolbar-mobile"]';
  var SEL_SR = '.sr-only, .visually-hidden, .visually-hidden--inline, .hdt-sr-only';

  var observer = null;
  var syncing = false;
  var frame = null;

  function root() {
    return document.getElementById('hdt-page-cart');
  }

  function isVisible(el) {
    if (!el) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
    return el.getBoundingClientRect().height > 0;
  }

  function realCheckout() {
    for (var i = 0; i < CHECKOUT_SELECTORS.length; i++) {
      var el = document.querySelector(CHECKOUT_SELECTORS[i]);
      if (isVisible(el)) return el;
    }
    return document.querySelector(SEL_CHECKOUT_FALLBACK);
  }

  function subtotalText() {
    var el = document.querySelector(SEL_TOTAL) || document.querySelector(SEL_TOTAL_FALLBACK);
    if (!el) return '';
    var clone = el.cloneNode(true);
    var sr = clone.querySelectorAll(SEL_SR);
    for (var i = 0; i < sr.length; i++) {
      if (sr[i].parentNode) sr[i].parentNode.removeChild(sr[i]);
    }
    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      '#' + BAR_ID + '{display:none}' +
      '@media (max-width:767px){' +
      '#' + BAR_ID + '{position:fixed;left:0;right:0;z-index:50;display:flex;align-items:center;' +
      'gap:12px;padding:10px 15px;background:rgb(var(--color-background,255 255 255));' +
      'border-top:1px solid rgb(var(--color-line-border,235 235 235));' +
      'box-shadow:0 -2px 12px rgba(0,0,0,.10)}' +
      '#' + BAR_ID + '[hidden]{display:none}' +
      '#' + BAR_ID + ' .tu-total{display:flex;flex-direction:column;line-height:1.25;flex:0 0 auto}' +
      '#' + BAR_ID + ' .tu-total-label{font-size:12px;opacity:.7}' +
      '#' + BAR_ID + ' .tu-total-value{font-size:16px;font-weight:600;white-space:nowrap}' +
      '#' + BAR_ID + ' .tu-btn{flex:1 1 auto;min-height:45px;display:flex;align-items:center;' +
      'justify-content:center;gap:8px;padding:5px 10px;border:0;border-radius:0;background:#000;' +
      'color:#fff;font-size:14px;font-weight:400;line-height:1.2;cursor:pointer}' +
      '#' + BAR_ID + ' .tu-btn span{color:#fff}' +
      '#' + BAR_ID + ' .tu-btn .btn-text{display:inline-flex;align-items:center;gap:4px}' +
      '#' + BAR_ID + ' .tu-btn .pay-opt-icon{display:inline-flex;align-items:center;gap:6px}' +
      '#' + BAR_ID + ' .tu-btn .addloadr,#' + BAR_ID + ' .tu-btn .cir-loader{display:none!important}' +
      '#' + BAR_ID + ' .tu-btn[disabled]{opacity:.55;pointer-events:none}' +
      'body.tu-has-sticky-cart #hdt-page-cart{padding-bottom:110px}' +
      '}';
    document.head.appendChild(s);
  }

  function toolbarOffset() {
    var el = document.querySelector(SEL_TOOLBAR);
    if (!el) return 0;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.position !== 'fixed') return 0;
    return Math.round(el.getBoundingClientRect().height) || 0;
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function mirrorButton(btn, real) {
    if (!btn || !real) return;
    var html = real.innerHTML || '';
    if (btn.getAttribute('data-mirrored') !== html) {
      btn.setAttribute('data-mirrored', html);
      btn.innerHTML = html;
      var withId = btn.querySelectorAll('[id]');
      for (var i = 0; i < withId.length; i++) withId[i].removeAttribute('id');
      var loaders = btn.querySelectorAll('.addloadr');
      for (var j = 0; j < loaders.length; j++) {
        if (loaders[j].parentNode) loaders[j].parentNode.removeChild(loaders[j]);
      }
      if (!btn.textContent.trim() && !btn.querySelector('img')) btn.textContent = 'Checkout';
    }

    var disabled = real.disabled === true || real.classList.contains('disabled');
    if (btn.disabled !== disabled) btn.disabled = disabled;
  }

  function buildBar() {
    var bar = document.getElementById(BAR_ID);
    if (bar) return bar;

    bar = document.createElement('div');
    bar.id = BAR_ID;
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Cart summary');

    var total = document.createElement('div');
    total.className = 'tu-total';

    var label = document.createElement('span');
    label.className = 'tu-total-label';
    label.textContent = 'Subtotal';

    var value = document.createElement('span');
    value.className = 'tu-total-value';

    total.appendChild(label);
    total.appendChild(value);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tu-btn';
    btn.addEventListener('click', function () {
      var real = realCheckout();
      if (real) real.click();
    });

    bar.appendChild(total);
    bar.appendChild(btn);

    document.body.appendChild(bar);
    return bar;
  }

  function doSync() {
    var real = realCheckout();
    var bar = buildBar();

    if (!real) {
      if (!bar.hidden) bar.hidden = true;
      document.body.classList.remove('tu-has-sticky-cart');
      return;
    }

    if (bar.hidden) bar.hidden = false;
    document.body.classList.add('tu-has-sticky-cart');

    mirrorButton(bar.querySelector('.tu-btn'), real);
    setText(bar.querySelector('.tu-total-value'), subtotalText());

    var bottom = toolbarOffset() + 'px';
    if (bar.style.bottom !== bottom) bar.style.bottom = bottom;
  }

  function observe() {
    var target = root();
    if (observer && target) {
      observer.observe(target, { childList: true, subtree: true });
    }
  }

  function sync() {
    if (syncing) return;
    syncing = true;
    if (observer) observer.disconnect();
    try { doSync(); } catch (e) {}
    observe();
    syncing = false;
  }

  function schedule() {
    if (frame) return;
    frame = true;
    var ran = false;
    var run = function () {
      if (ran) return;
      ran = true;
      frame = null;
      sync();
    };
    window.requestAnimationFrame(run);
    window.setTimeout(run, 250);
  }

  function init() {
    if (!root()) return;
    injectStyles();
    observer = new MutationObserver(schedule);
    sync();
    window.addEventListener('resize', schedule);
    window.addEventListener('gokwikLoaded', schedule);

    var ticks = 0;
    var settle = window.setInterval(function () {
      ticks++;
      schedule();
      if (ticks > 20) window.clearInterval(settle);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
